import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errText } from "@/lib/api";

export const DECISION = {
  approved: { label: "Exception approved", cls: "bg-emerald-100 text-emerald-900", Icon: ShieldCheck },
  rejected: { label: "Exception rejected", cls: "bg-red-100 text-red-800", Icon: ShieldX },
};

export const SpotDecisionDialog = ({ r, decision, onClose, onSaved }) => {
  const [comment, setComment] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const approve = decision === "approved";
  const save = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/receipts/${r.id}/spot-decision`, { decision, comment: comment.trim(), notify });
      onSaved(res.data);
      toast.success(`Spot exception ${decision}${res.data.payment?.spot_decision?.notified ? " · counsellor emailed" : ""}`);
      onClose();
    } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!decision} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="spot-decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{approve ? "Approve" : "Reject"} Spot exception · {r.receipt_no}</DialogTitle>
          <DialogDescription>
            {approve ? "The Spot discount stays as it is." : "The counsellor will be asked to switch this receipt to Revisit so the fees are recalculated."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-900">
            <b>Rule broken:</b> {r.payment.spot_issues.join("; ")}<br /><b>Counsellor's reason:</b> {r.payment.spot_note || "—"}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Your comment *</Label>
            <Textarea rows={3} maxLength={500} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="spot-decision-comment" />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="spot-decision-notify" />Email the counsellor about this decision
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || comment.trim().length < 3} onClick={save} data-testid="spot-decision-confirm-btn"
            className={approve ? "bg-emerald-700 hover:bg-emerald-800" : "bg-red-600 hover:bg-red-700"}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{approve ? "Approve" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
